// Sales (Sales Kit and Outcomes): typed routes. Verified against src/aithora/v3/sales.ts
// (mounted on /api/aithora). The CRM routes these tabs also read (crmProspects,
// crmFacilitySearch, crmDeals, crmCounts, crmGmailAccounts) live in sales-routes.ts.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  DemoProofLinesResponse,
  LiveProofLinesResponse,
  SalesKitResponse,
  SalesKitSearchResponse,
  SalesOutcomesResponse,
  SalesPackBody,
  SalesPackResult,
  SalesPilotResponse,
} from './sales-kit-types';

const A = AITHORA_PREFIX;

/** GET /sales/kit?doc=: every non-investor section of the Sales Kit (copy edits need no deploy). */
export const salesKit = route<SalesKitResponse, { doc?: string }>()('GET', `${A}/sales/kit`, { honest: true });
/** GET /sales/kit/search?q=&kind=: "In a call" search (each word, or a synonym, must appear). */
export const salesKitSearch = route<SalesKitSearchResponse, { q: string; kind?: string }>()('GET', `${A}/sales/kit/search`, { honest: true });
/** POST /sales/packs {facilityId, docs[]}: send a prospect pack from the rep's own mailbox. 409 = connect a mailbox, 503 = not built yet. */
export const salesPackSend = route<SalesPackResult, void, SalesPackBody>()('POST', `${A}/sales/packs`, { acceptStatuses: [409, 503] });

/** GET /sales/pilots/:dealId: day X of 90, the go / no-go date, the phase, the Week 1 metrics and the look-back. */
export const salesPilot = route<SalesPilotResponse>()('GET', `${A}/sales/pilots/:dealId`, { honest: true });
/** The pilot building's measured outcomes since the pilot started (metrics/pilot-metrics.ts; the fields the page reads). */
export interface SalesPilotOutcomes {
  real: boolean;
  reason?: string;
  deal_id?: string;
  organization?: { id: string; name: string | null };
  metrics?: {
    range: { from: string; to: string; days: number };
    enrollment: { residents: number; consented: number };
    engagement: { interactions: { total: number; perResidentPerDay: number } };
    isolation: { delta: number };
    reimbursement: { ccmCaptured: number; claims: number };
    safety: { alerts: number; escalations: number; interventions: number; apsReferrals: number };
    [k: string]: unknown;
  };
}
/** GET /sales/pilots/:dealId/outcomes: restored from production's Pilot Outcomes; real:false with the reason until the building is linked. */
export const salesPilotOutcomes = route<SalesPilotOutcomes>()('GET', `${A}/sales/pilots/:dealId/outcomes`);
/** GET /sales/proof-lines?mode=live: real lines the server allows (day 90 + reference consent, or an early read for that facility). */
export const salesProofLinesLive = route<LiveProofLinesResponse, { mode: 'live'; facilityId?: string }>()('GET', `${A}/sales/proof-lines`, { honest: true });
/** GET /sales/proof-lines?mode=demo: the report's demonstration lines and the label they carry. */
export const salesProofLinesDemo = route<DemoProofLinesResponse, { mode: 'demo' }>()('GET', `${A}/sales/proof-lines`, { honest: true });
/** GET /sales/outcomes?mode=live|demo: the portfolio figures, the proof rows and (demo) the facilities. */
export const salesOutcomes = route<SalesOutcomesResponse, { mode: 'live' | 'demo' }>()('GET', `${A}/sales/outcomes`, { honest: true });
